import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Sparkles, BookOpen, Headphones, Library, Users, Brain, ChevronRight } from 'lucide-react';

/* ------------------------------------------------------------------ */
/*  Wave Background (matches HomePageV3)                                */
/* ------------------------------------------------------------------ */
const WaveBg = ({ className = '', flip = false }) => (
  <div className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`} aria-hidden="true">
    <svg className={`absolute w-full h-full opacity-25 ${flip ? 'rotate-180' : ''}`} preserveAspectRatio="none" viewBox="0 0 1440 400">
      <path d="M0,80 C360,160 720,20 1080,100 C1260,140 1380,60 1440,80 L1440,0 L0,0 Z" fill="url(#wg1)" />
      <path d="M0,180 C240,120 480,200 720,140 C960,80 1200,180 1440,140 L1440,400 L0,400 Z" fill="url(#wg2)" />
      <defs>
        <linearGradient id="wg1" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#BAE6FD" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#C7D2FE" stopOpacity="0.3" />
        </linearGradient>
        <linearGradient id="wg2" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#DBEAFE" stopOpacity="0.2" />
          <stop offset="100%" stopColor="#EDE9FE" stopOpacity="0.15" />
        </linearGradient>
      </defs>
    </svg>
  </div>
);

/* ------------------------------------------------------------------ */
/*  Feature data                                                        */
/* ------------------------------------------------------------------ */
const FEATURES = [
  {
    title: 'Curated Content',
    desc: 'AI-powered curation from top medical journals, tailored to your specialty and research interests.',
    icon: <BookOpen className="w-7 h-7" />,
    gradient: 'from-sky-400 to-cyan-500',
    bg: 'bg-sky-50',
    text: 'text-sky-600',
  },
  {
    title: 'AI Summaries',
    desc: 'Key findings, study design, and clinical bottom-line — generated instantly so you can triage faster.',
    icon: <Sparkles className="w-7 h-7" />,
    gradient: 'from-amber-400 to-orange-500',
    bg: 'bg-amber-50',
    text: 'text-amber-600',
  },
  {
    title: 'Audio Digests',
    desc: 'Listen to article summaries on the go. Turn your commute into a learning session.',
    icon: <Headphones className="w-7 h-7" />,
    gradient: 'from-violet-500 to-purple-600',
    bg: 'bg-violet-50',
    text: 'text-violet-600',
  },
  {
    title: 'Personal Library',
    desc: 'Save, organize by topic, annotate with notes, and export your curated collection anytime.',
    icon: <Library className="w-7 h-7" />,
    gradient: 'from-emerald-400 to-teal-500',
    bg: 'bg-emerald-50',
    text: 'text-emerald-600',
  },
  {
    title: 'Specialty Community',
    desc: 'Discuss findings with verified peers in your field. Share insights, not patient data.',
    icon: <Users className="w-7 h-7" />,
    gradient: 'from-rose-400 to-pink-500',
    bg: 'bg-rose-50',
    text: 'text-rose-600',
  },
  {
    title: 'AI Deep Dive',
    desc: 'Ask questions, compare studies, and generate evidence briefs — powered by LitScholar AI.',
    icon: <Brain className="w-7 h-7" />,
    gradient: 'from-indigo-500 to-blue-700',
    bg: 'bg-indigo-50',
    text: 'text-indigo-600',
  },
];

/* ------------------------------------------------------------------ */
/*  How It Works steps                                                  */
/* ------------------------------------------------------------------ */
const STEPS = [
  {
    num: '01',
    title: 'Set Your Preferences',
    desc: 'Choose your specialty, subspecialties, topics, and preferred journals.',
    color: 'from-sky-400 to-cyan-500',
  },
  {
    num: '02',
    title: 'Receive Curated Digests',
    desc: 'Get personalized literature digests with AI summaries on your schedule.',
    color: 'from-violet-500 to-purple-600',
  },
  {
    num: '03',
    title: 'Build & Explore',
    desc: 'Save articles, listen to audio summaries, and deep-dive with AI analysis.',
    color: 'from-emerald-400 to-teal-500',
  },
];

/* ================================================================== */
/*  LANDING PAGE                                                        */
/* ================================================================== */
const LandingPage = () => {
  return (
    <div className="min-h-screen bg-white overflow-hidden">

      {/* ============================================================ */}
      {/*  HERO SECTION                                                  */}
      {/* ============================================================ */}
      <section className="relative py-20 sm:py-28 lg:py-32">
        {/* Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-sky-50 via-blue-50/60 to-white" />
        <WaveBg />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 text-center">
          {/* Logo */}
          <div className="inline-flex items-center gap-2.5 mb-8">
            <div className="w-10 h-10 bg-gradient-to-br from-sky-400 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg shadow-sky-200/50">
              <Heart className="w-5 h-5 text-white fill-white" />
            </div>
            <span className="text-2xl font-bold bg-gradient-to-r from-sky-600 to-cyan-600 bg-clip-text text-transparent">
              LitCurrent
            </span>
          </div>

          {/* Headline */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 leading-[1.1] mb-6">
            Your Clinical Companion for{' '}
            <span className="bg-gradient-to-r from-sky-500 via-cyan-500 to-teal-400 bg-clip-text text-transparent">
              Evidence&#8209;Based Care
            </span>
          </h1>

          <p className="max-w-2xl mx-auto text-lg sm:text-xl text-slate-600 leading-relaxed mb-10">
            Evidence updates from the journals that matter to your practice. Read, listen, or deep&#8209;dive—whenever it fits your schedule.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
            <Link
              to="/signup"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-base font-semibold text-white bg-gradient-to-r from-sky-500 to-cyan-500 hover:from-sky-600 hover:to-cyan-600 shadow-lg shadow-sky-200/50 hover:shadow-xl hover:shadow-sky-200/60 transition-all duration-200"
              data-testid="get-started-btn"
            >
              Get Started Free
              <ArrowRight className="w-4.5 h-4.5" />
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-base font-semibold text-slate-700 bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 shadow-sm transition-all duration-200"
            >
              Sign In
            </Link>
          </div>

          {/* Trust signal */}
          <p className="mt-8 text-sm text-slate-400">
            PHI-Zero compliant · No patient data ever stored
          </p>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  FEATURES SECTION                                              */}
      {/* ============================================================ */}
      <section className="relative py-20 sm:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          {/* Section header */}
          <div className="text-center mb-14">
            <span className="inline-block text-sm font-semibold text-sky-600 bg-sky-50 px-4 py-1.5 rounded-full mb-4">
              Everything you need
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              AI-curated research digests, audio summaries, and deep-dive analysis — personalized for your specialty.
            </h2>
            <p className="max-w-xl mx-auto text-slate-500 text-lg">
              Built for clinicians by clinicians.
            </p>
          </div>

          {/* Feature cards — 3x2 grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map(({ title, desc, icon, gradient, bg, text }) => (
              <div
                key={title}
                className="group relative bg-white rounded-2xl border border-slate-100 p-6 hover:shadow-xl hover:shadow-slate-200/40 hover:-translate-y-1 transition-all duration-300"
              >
                {/* Icon circle */}
                <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl ${bg} ${text} mb-4 transition-transform duration-300 group-hover:scale-110`}>
                  {icon}
                </div>

                {/* Content */}
                <h3 className="text-lg font-bold text-slate-800 mb-2">{title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{desc}</p>

                {/* Gradient accent line */}
                <div className={`absolute bottom-0 left-6 right-6 h-0.5 bg-gradient-to-r ${gradient} rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  HOW IT WORKS                                                  */}
      {/* ============================================================ */}
      <section className="relative py-20 sm:py-24">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-50/80 to-white" />
        <WaveBg className="opacity-10" flip />

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14">
            <span className="inline-block text-sm font-semibold text-violet-600 bg-violet-50 px-4 py-1.5 rounded-full mb-4">
              Simple to start
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              How It Works
            </h2>
          </div>

          <div className="grid sm:grid-cols-3 gap-8">
            {STEPS.map(({ num, title, desc, color }) => (
              <div key={num} className="text-center">
                {/* Step number */}
                <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br ${color} text-white text-lg font-bold shadow-lg mb-5`}>
                  {num}
                </div>
                <h3 className="text-lg font-bold text-slate-800 mb-2">{title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>

          {/* Connector line (visible on sm+) */}
          <div className="hidden sm:block absolute top-[calc(50%-8px)] left-[20%] right-[20%] h-px bg-gradient-to-r from-sky-200 via-violet-200 to-emerald-200 -z-10" />
        </div>
      </section>

      {/* ============================================================ */}
      {/*  SOCIAL PROOF / TRUST                                          */}
      {/* ============================================================ */}
      <section className="py-16 sm:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="bg-gradient-to-br from-sky-50 via-blue-50/50 to-indigo-50 rounded-3xl p-8 sm:p-12 text-center border border-sky-100/50">
            <div className="inline-flex items-center gap-2 text-sky-600 mb-4">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 1L12.47 6.6L18.5 7.35L14.25 11.4L15.4 17.5L10 14.6L4.6 17.5L5.75 11.4L1.5 7.35L7.53 6.6L10 1Z" fill="currentColor"/></svg>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 1L12.47 6.6L18.5 7.35L14.25 11.4L15.4 17.5L10 14.6L4.6 17.5L5.75 11.4L1.5 7.35L7.53 6.6L10 1Z" fill="currentColor"/></svg>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 1L12.47 6.6L18.5 7.35L14.25 11.4L15.4 17.5L10 14.6L4.6 17.5L5.75 11.4L1.5 7.35L7.53 6.6L10 1Z" fill="currentColor"/></svg>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 1L12.47 6.6L18.5 7.35L14.25 11.4L15.4 17.5L10 14.6L4.6 17.5L5.75 11.4L1.5 7.35L7.53 6.6L10 1Z" fill="currentColor"/></svg>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M10 1L12.47 6.6L18.5 7.35L14.25 11.4L15.4 17.5L10 14.6L4.6 17.5L5.75 11.4L1.5 7.35L7.53 6.6L10 1Z" fill="currentColor"/></svg>
            </div>
            <blockquote className="text-xl sm:text-2xl font-semibold text-slate-800 leading-snug mb-4">
              "LitCurrent saves me hours every week. The AI summaries are remarkably accurate and the audio digests are perfect for my commute."
            </blockquote>
            <p className="text-sm text-slate-500">
              — Cardiologist, Academic Medical Center
            </p>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  FINAL CTA                                                     */}
      {/* ============================================================ */}
      <section className="relative py-20 sm:py-28">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
        {/* Subtle dot pattern */}
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'radial-gradient(circle, white 1px, transparent 1px)', backgroundSize: '24px 24px' }} />

        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Stay Current?
          </h2>
          <p className="text-lg text-slate-300 mb-8 leading-relaxed">
            Join thousands of clinicians, researchers, and scientists who trust LitCurrent to keep them ahead.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/signup"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-base font-semibold text-slate-900 bg-white hover:bg-slate-50 shadow-lg transition-all duration-200"
            >
              Create Your Free Account
              <ArrowRight className="w-4.5 h-4.5" />
            </Link>
          </div>
          <p className="mt-6 text-xs text-slate-500">
            Powered by PubMed · PHI-Zero · No patient data ever stored
          </p>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  FOOTER                                                        */}
      {/* ============================================================ */}
      <footer className="bg-white border-t border-slate-100 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-gradient-to-br from-sky-400 to-cyan-500 rounded-lg flex items-center justify-center">
              <Heart className="w-3 h-3 text-white fill-white" />
            </div>
            <span className="text-sm font-semibold text-slate-600">LitCurrent by Scienthesis</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-slate-400">
            <Link to="/phi-guidelines" className="hover:text-slate-600 transition-colors">PHI-Zero Policy</Link>
            <span>© {new Date().getFullYear()} Scienthesis</span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
